import { reportError } from "./sentry-reporter";

export interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry?: number; // milliseconds
}

export class CacheManager {
  private static instance: CacheManager;
  private defaultExpiry = 1000 * 60 * 60; // 1 hour

  private constructor() {}

  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  public async setItem<T>(key: string, data: T, expiry?: number): Promise<void> {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiry: expiry || this.defaultExpiry,
    };
    await OfficeRuntime.storage.setItem(key, JSON.stringify(cacheItem));
  }

  public async getItem<T>(key: string): Promise<T | null> {
    try {
      const item = await OfficeRuntime.storage.getItem(key);
      if (!item) return null;

      const cacheItem: CacheItem<T> = JSON.parse(item);

      if (cacheItem.expiry && Date.now() - cacheItem.timestamp > cacheItem.expiry) {
        await this.removeItem(key);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.error("Error retrieving item from cache:", error);
      reportError(error, { level: "warning", extra: { component: "cacheManager", action: "get_item", key } });
      return null;
    }
  }

  public async removeItem(key: string): Promise<void> {
    await OfficeRuntime.storage.removeItem(key);
  }

  public async clearCache(): Promise<void> {
    try {
      const keys = await OfficeRuntime.storage.getKeys();
      if (keys && keys.length > 0) {
        await OfficeRuntime.storage.removeItems(keys);
      }
    } catch (error) {
      console.error("Error clearing cache:", error);
      reportError(error, { level: "warning", extra: { component: "cacheManager", action: "clear_cache" } });
    }
  }
}

export const cacheManager = CacheManager.getInstance(); 